import { useEffect, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import { AGENT_ID_QUERY_KEY } from '~/features/agents/constants'
import useGetAgent from '~/features/agents/hooks/useGetAgent'
import useGetAgentsByOrgId from '~/features/agents/hooks/useGetAgentsByOrgId'
import { useSetAgentId } from '~/features/agents/hooks/useSetAgentId'
import useUpdateAgent from '~/features/agents/hooks/useUpdateAgent'
import { TAgent } from '~/features/agents/models'
import { getDefaultAgentData } from '~/features/agents/utils'
import useGetAuthSessionQuery from '~/features/auth/hooks/useGetAuthSessionQuery'
import { EHttpErrorName } from '~/models/api'
import { sessionStore, store } from '~/store'

const useGetAgentCentralized = () => {
  const { data: authSession, isLoading: isLoadingAuthSession } =
    useGetAuthSessionQuery()
  const userOrgId = store((state) => state.user.organizationId)
  const [stateAgentId, setAgentInState] = sessionStore((state) => [
    state.agent.id || '',
    state.setAgent,
  ])
  const [searchParams] = useSearchParams()
  const searchParamsAgentId = searchParams.get(AGENT_ID_QUERY_KEY)
  const setAgentId = useSetAgentId()

  const {
    data: agents,
    isSuccess: isGetAgentsSuccessful,
    error,
    isError,
  } = useGetAgentsByOrgId(userOrgId)

  const {
    data: agentById,
    isSuccess: isGetAgentByIdSuccessful,
    isError: isAgentByIdIsError,
  } = useGetAgent({
    agentId: userOrgId && !isError ? '' : searchParamsAgentId || stateAgentId,
  })

  const { mutate: updateAgentMutation, isPending: isPendingUpdate } =
    useUpdateAgent()

  const agentId = searchParamsAgentId || stateAgentId || agents?.data?.[0]?.id

  useEffect(() => {
    if (!authSession?.cognitoUid && !isLoadingAuthSession) {
      setAgentId(undefined)
      setAgentInState({ id: undefined })
    }
  }, [
    authSession?.cognitoUid,
    isLoadingAuthSession,
    setAgentId,
    setAgentInState,
  ])

  useEffect(() => {
    if (!isGetAgentsSuccessful || !authSession?.cognitoUid) {
      return
    }

    if (!agents?.data?.length) {
      if (stateAgentId || searchParamsAgentId) {
        setAgentId(undefined)
        setAgentInState({ id: undefined })
      }

      toast.error('No agents found')
      return
    }

    if (!searchParamsAgentId) {
      if (!stateAgentId) {
        setAgentId(agents?.data[0].id)
        setAgentInState({ id: agents?.data[0].id })

        return
      }

      const correspondingAgentIdFromState =
        agents.data.find((agent) => agent.id === stateAgentId)?.id || undefined
      setAgentId(correspondingAgentIdFromState)
      if (!correspondingAgentIdFromState) {
        setAgentInState({ id: agents?.data[0].id })
      }
      return
    }

    if (!stateAgentId || stateAgentId !== searchParamsAgentId) {
      setAgentInState({ id: searchParamsAgentId })
    }
  }, [
    agents,
    isGetAgentsSuccessful,
    stateAgentId,
    setAgentId,
    searchParamsAgentId,
    setAgentInState,
    authSession,
    isLoadingAuthSession,
  ])

  useEffect(() => {
    if (error?.name === EHttpErrorName.agentNotFound && agentId && userOrgId) {
      if (!!agentById?.data && !agentById?.data.orgId) {
        updateAgentMutation({
          agentId,
          payload: { orgId: userOrgId },
          hideToast: true,
        })
      }
    }
  }, [agentById?.data, agentId, error?.name, updateAgentMutation, userOrgId])

  let isLoading = false

  if (userOrgId) {
    isLoading =
      !isGetAgentsSuccessful &&
      (error as Error | null)?.name !== EHttpErrorName.agentNotFound
  } else if (searchParamsAgentId || stateAgentId) {
    isLoading = !isGetAgentByIdSuccessful
  } else {
    isLoading = !isGetAgentsSuccessful && !isGetAgentByIdSuccessful
  }

  isLoading = isLoading || isPendingUpdate

  const selectedAgent: TAgent | undefined = useMemo(() => {
    if (agents?.data && !!authSession?.cognitoUid) {
      return getDefaultAgentData(
        agents?.data?.find((agent) => agent.id === agentId),
      )
    }

    return getDefaultAgentData(agentById?.data || undefined)
  }, [agentById?.data, agentId, agents?.data, authSession?.cognitoUid])

  return useMemo(() => {
    return {
      selectedAgent,
      agents: agents?.data,
      isError: userOrgId ? isError : isAgentByIdIsError,
      isSuccess: userOrgId ? isGetAgentsSuccessful : isGetAgentByIdSuccessful,
      isLoading,
    }
  }, [
    agents?.data,
    isAgentByIdIsError,
    isError,
    isGetAgentByIdSuccessful,
    isGetAgentsSuccessful,
    isLoading,
    selectedAgent,
    userOrgId,
  ])
}

export default useGetAgentCentralized
